import React, { useState, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as NavLink } from "react-scroll";

// Helper function to get AppBar height
const getAppBarHeight = () => 64;

export default function Navigation() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isBigScreen = useMediaQuery({ query: '(min-width: 900px)' });
  const appBarHeight = getAppBarHeight();

  const navigationItems = [
    {title:"Software",link:"software-solutions-section"},
    {title:"Data Science",link:"data-science-section"},
    {title:"Team",link:"team-section"},
    {title:"Research",link:"research-section"},
    {title:"Contact",link:"contact-section"}
  ];

  const renderAppBar = useMemo(() => (
    <AppBar 
      position="fixed" 
      className='appbar' 
      sx={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(37, 97, 179, 0.1)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease'
      }}
    >
      <Toolbar sx={{ 
        paddingX: { xs: 2, sm: 4 },
        justifyContent: 'center'
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          maxWidth: '1450px',
          marginX: { xs: 0, sm: 0, md: 0 }
        }}>
          <NavLink 
            to={"hero-section"} 
            spy 
            smooth 
            offset={-appBarHeight} 
            duration={500} 
            className='barLink'
            style={{ 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}>
              <img 
                src="datasky_logo.png" 
                className="App-logo" 
                alt="Datasky Logo" 
                style={{
                  height: 45, 
                  marginRight: 12,
                  filter: 'drop-shadow(0 2px 8px rgba(37, 97, 179, 0.3))'
                }}
              />
              {/* <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #2561b3 0%, #1e40af 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  fontSize: '1.3rem',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                datasky
              </Typography> */}
            </Box>
          </NavLink>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}> 
            {navigationItems.map((linkData) => 
              <NavLink 
                className='barLink' 
                key={linkData.title} 
                activeClass='active' 
                spy 
                smooth 
                to={linkData.link} 
                offset={-appBarHeight} 
                duration={500}
                style={{
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                <Box sx={{
                  padding: '8px 16px',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(37, 97, 179, 0.2)'
                  },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.05) 0%, transparent 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  },
                  '&:hover:before': {
                    opacity: 1
                  }
                }}>
                  <Typography sx={{
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    color: '#1e293b',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: '#2561b3'
                    }
                  }}>
                    {linkData.title}
                  </Typography>
                </Box>
              </NavLink> 
            )}
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  ), [appBarHeight, navigationItems]);

  const renderMobileDrawer = useMemo(() => (
    <Drawer 
      open={isDrawerOpen} 
      onClose={() => setIsDrawerOpen(false)}
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderLeft: '1px solid rgba(37, 97, 179, 0.1)',
          boxShadow: '0 0 30px rgba(0,0,0,0.1)'
        }
      }}
    >
      <Box sx={{ 
        width: 280, 
        padding: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }} role="presentation">
        {/* Mobile menu header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 4,
          paddingBottom: 2,
          borderBottom: '1px solid rgba(37, 97, 179, 0.1)'
        }}>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #2561b3 0%, #1e40af 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}>
            Navigation
          </Typography>
          <IconButton 
            onClick={() => setIsDrawerOpen(false)}
            sx={{ 
              color: '#64748b',
              '&:hover': {
                backgroundColor: 'rgba(37, 97, 179, 0.1)',
                color: '#2561b3'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <List sx={{ flexGrow: 1 }}>
          {navigationItems.map((linkData, l) => 
            <ListItem key={"navitem-"+l} disablePadding sx={{ marginBottom: 1 }}>
              <NavLink 
                className='barLink' 
                key={linkData.title} 
                activeClass='active' 
                spy 
                smooth 
                to={linkData.link} 
                offset={-appBarHeight} 
                duration={500} 
                onClick={() => setIsDrawerOpen(false)} 
                style={{
                  display: 'block', 
                  width: '100%',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                <Box sx={{
                  padding: '12px 16px',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 2px 8px rgba(37, 97, 179, 0.15)'
                  }
                }}>
                  <Typography sx={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#1e293b',
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: '#2561b3'
                    }
                  }}>
                    {linkData.title}
                  </Typography>
                </Box>
              </NavLink> 
            </ListItem>
          )}
        </List>
      </Box>
    </Drawer>
  ), [isDrawerOpen, appBarHeight, navigationItems]);

  const effectiveAppBar = isBigScreen ? renderAppBar : (
    <AppBar 
      position="fixed" 
      className='appbar' 
      sx={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(37, 97, 179, 0.1)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        color: '#1e293b'
      }}
    >
      <Toolbar sx={{ 
        paddingX: 2,
        justifyContent: 'center'
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          maxWidth: '1450px',
          marginX: { xs: 0, sm: 0, md: 0 }
        }}>
          <IconButton 
            edge="start" 
            color="inherit" 
            aria-label="menu" 
            onClick={() => setIsDrawerOpen(true)}
            sx={{
              color: '#64748b',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(37, 97, 179, 0.1)',
                color: '#2561b3',
                transform: 'scale(1.1)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}>
            <img 
              src="datasky_logo.png" 
              className="App-logo" 
              alt="Datasky Logo" 
              style={{
                height: 40,
                marginRight: 8,
                filter: 'drop-shadow(0 2px 8px rgba(37, 97, 179, 0.3))'
              }}
            />
   
          </Box>
        </Box>
      </Toolbar>
      {renderMobileDrawer}
    </AppBar>
  );

  return effectiveAppBar;
} 